// The embedded join box on the video page joins through bar the same way
// signup.<site> (atm-static) does: GET /join/data for the payment methods and
// price points, then a form POST to /join/submit, which redirects to the
// biller. These helpers mirror atm-static's join page logic. Unlike that page,
// the box never asks for a username or password. On sites listed in
// join_box_email_sites, card joins ask for the email and post it with
// credentials made up here (see asksForEmail). Everything else posts
// credentials=generated: bar makes up the username and password, and the
// biller's page collects the email.

// The bar site a tour site joins through. signup.<domain> serves atm-static's
// page for that bar site, so the join URL names it.
export const barSiteDomain = (site) => {
  try {
    const joinUrl = site.alias?.natsJoinUrl ?? site.natsJoinUrl
    return new URL(joinUrl).hostname.replace(/^signup\./, "")
  } catch {
    return site.host
  }
}

const BASE36 = "abcdefghijklmnopqrstuvwxyz0123456789"

export const randomBase36 = (length) =>
  Array.from(
    crypto.getRandomValues(new Uint8Array(length)),
    (byte) => BASE36[byte % 36],
  ).join("")

// The visitor never sees these; the biller's approval emails them. Same shape
// as atm-static's: 5 characters of the site's short name and 9 random ones.
export const joinCredentials = (shortName, random = randomBase36) => ({
  username: `${shortName.slice(0, 5)}${random(9)}`,
  password: random(10),
})

// Card joins start at RocketGate, which recognises a returning customer by the
// email in its link and lets them pay with their saved card (RocketPay).
// Without an email in the link, every visitor types in a full card.
export const asksForEmail = (cascadeId) => Boolean(cascadeId?.startsWith("cc_"))

const isCheck = (cascade) => cascade.id.startsWith("check")
const isPayPal = (cascade) => cascade.id.startsWith("paypal")

// PayPal always comes before check, wherever bar lists check.
const payPalBeforeCheck = (cascades) => {
  const checks = cascades.filter(isCheck)
  const rest = cascades.filter((cascade) => !isCheck(cascade))
  const lastPayPal = rest.findLastIndex(isPayPal)
  if (lastPayPal === -1) return cascades

  return [
    ...rest.slice(0, lastPayPal + 1),
    ...checks,
    ...rest.slice(lastPayPal + 1),
  ]
}

// Gift cards (paygarden) get their own form on the signup page and aren't
// offered here.
export const paymentMethods = (cascades = [], language = "en") =>
  payPalBeforeCheck(
    cascades.filter((cascade) => cascade.id !== "paygarden"),
  ).map((cascade) => ({
    id: cascade.id,
    label:
      cascade.description?.[language.toUpperCase()] ??
      cascade.description?.default ??
      cascade.id,
    icons: cascade.payment_icons ?? [],
  }))

// The sale banners signup.pornplus.com puts on discounted price points.
const SALE_LABELS = {
  "$17.95": "Limited time sale: 40% off!",
  "$14.95": "Limited time sale: 50% off!",
}

// The signup page strikes through the regular monthly price next to a
// discounted monthly membership.
const REGULAR_MONTHLY_PRICE = "$29.95"
const DISCOUNTED_MONTHLY_PRICES = ["$14.95", "$19.95"]

// "Month" reads as "/mo" next to a price; other periods ("Day") as written.
export const priceSuffix = (period = "") =>
  period.toLowerCase() === "month" ? "mo" : period.toLowerCase()

const pricePointCard = (pricePoint) => ({
  id: pricePoint.id,
  title: pricePoint.description?.trim(),
  price: pricePoint.display_price,
  period: priceSuffix(pricePoint.display_period),
  // What the biller charges today, the whole term on multi-month plans.
  // Every card shows it, even a free trial's $0.00.
  charge: pricePoint.normal_price,
  wasPrice:
    pricePoint.tags?.includes("monthly") &&
    DISCOUNTED_MONTHLY_PRICES.includes(pricePoint.normal_price)
      ? REGULAR_MONTHLY_PRICE
      : null,
  best: Boolean(pricePoint["best?"]),
  sale: pricePoint.tags?.includes("special")
    ? (SALE_LABELS[pricePoint.normal_price] ?? null)
    : null,
})

// signup.pornplus.com splits its memberships by what they include: three
// months or longer download and stream, shorter ones only stream. Other sites'
// signup pages list them together.
export const membershipGroups = (pricePoints = [], { splitByAccess }) => {
  if (!splitByAccess) {
    return [{ id: "all", pricePoints: pricePoints.map(pricePointCard) }]
  }

  return [
    {
      id: "full",
      title: "Download + stream",
      note: "High quality downloads and streaming.",
      pricePoints: pricePoints
        .filter((pricePoint) => pricePoint.initial_months >= 3)
        .map(pricePointCard),
    },
    {
      id: "streaming",
      title: "Stream only",
      note: "Streaming only. No downloads.",
      pricePoints: pricePoints
        .filter((pricePoint) => pricePoint.initial_months < 3)
        .map(pricePointCard),
    },
  ].filter((group) => group.pricePoints.length)
}

// The signup page preselects the monthly membership.
export const defaultPricePointId = (pricePoints = []) =>
  (pricePoints.find((pricePoint) => pricePoint["monthly?"]) ?? pricePoints[0])
    ?.id ?? null

export const complianceLines = (pricePoints = []) =>
  pricePoints.map(
    (pricePoint) =>
      `${pricePoint.compliance_description} ${pricePoint.rebill_amount}`,
  )

// Stricter than type="email": "a@b" passes the browser's check, and no biller
// takes it. The same rule the signup page uses.
export const isCompleteEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())

export const readCookie = (name, cookies = document.cookie) => {
  // A stan code can contain "=", so split on the first one only.
  const value = cookies
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1)
  if (!value) return null

  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

// For previewing the box outside production: ?sale=1 dresses the regular
// monthly membership up as the 50% off sale bar swaps in during a sale
// (SaleSpecialPricePoints), and ?sale=0 turns a sale membership back into the
// regular one. Only the displayed prices change, so the result must never be
// submitted.
const SALE_PRICE = "$14.95"

const withPrice = (pricePoint, price, tags) => ({
  ...pricePoint,
  display_price: price,
  normal_price: price,
  rebill_amount: price,
  tags,
})

export const simulateSale = (pricePoints = [], sale) => {
  if (sale !== "1" && sale !== "0") return pricePoints

  return pricePoints.map((pricePoint) => {
    const tags = pricePoint.tags ?? []
    if (!tags.includes("monthly")) return pricePoint

    const onSale = tags.includes("special")
    if (sale === "1" && !onSale) {
      return withPrice(pricePoint, SALE_PRICE, [...tags, "special"])
    }
    if (sale === "0" && onSale) {
      return withPrice(
        pricePoint,
        REGULAR_MONTHLY_PRICE,
        tags.filter((tag) => tag !== "special"),
      )
    }
    return pricePoint
  })
}
